import { useParams, Link, Navigate } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { SERVICES } from "@/data/site";
import { ArrowLeft, ArrowRight, Check, Server, ShieldCheck, Cpu, Barcode, ShoppingBag, Wrench, FileSignature, Network, Monitor } from "lucide-react";

const iconMap = {
  "yazilim-cozumleri": Cpu,
  "pos-sistemleri": Monitor,
  "barkod-sistemleri": Barcode,
  "guvenlik-sistemleri": ShieldCheck,
  "teknik-servis": Wrench,
  "sunucu-kiralama": Server,
  "e-ticaret": ShoppingBag,
  "e-imza-kep": FileSignature,
  "network-kurulum": Network,
};

const accentColor = {
  red: "#FF3B30",
  blue: "#007AFF",
  green: "#34C759",
  black: "#FF3B30",
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = SERVICES.find((s) => s.slug === slug);
  if (!svc) return <Navigate to="/hizmetler" replace />;

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const Icon = iconMap[svc.slug] || Server;
  const iconColor = accentColor[svc.accent] || "#FF3B30";

  return (
    <>
      <SEO title={`${svc.title} — Pratik Teknoloji`} description={svc.short} />

      <PageHero
        badge={`Hizmet · ${svc.tag}`}
        title={<span className="inline-flex items-center gap-4 flex-wrap">
          <span
            className="h-16 w-16 md:h-20 md:w-20 rounded-2xl inline-flex items-center justify-center border align-middle"
            style={{ borderColor: `${iconColor}40`, background: `${iconColor}15`, color: iconColor }}
          >
            <Icon size={34} strokeWidth={1.7} />
          </span>
          <span>{svc.title}<span className="text-[#FF3B30]">.</span></span>
        </span>}
        subtitle={svc.short}
      >
        <Link to="/hizmetler" className="inline-flex items-center gap-2 text-sm font-semibold text-white/60 hover:text-white">
          <ArrowLeft size={16}/> Tüm Hizmetler
        </Link>
      </PageHero>

      {/* Body */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#FF3B30] mb-4">Kapsam</div>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed">{svc.long}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white/[0.03] border border-white/10 p-8 md:p-10">
              <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#FF3B30] mb-5">İçerik</div>
              <ul className="space-y-3">
                {svc.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <span className="mt-1 flex-none w-5 h-5 rounded-full bg-[#FF3B30] text-white inline-flex items-center justify-center"><Check size={12}/></span>
                    <span className="text-white font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA + next */}
      <section className="pb-24 md:pb-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] border border-white/10 text-white p-10 md:p-16 grid md:grid-cols-2 gap-8 items-center">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#FF3B30]/25 rounded-full blur-3xl pointer-events-none"></div>
            <div className="relative">
              <h3 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">Bu hizmet sizin için mi?</h3>
              <p className="mt-4 text-white/60 text-lg">Keşif toplantısı ücretsiz — ihtiyacınızı dinleyip size özel planı sunalım.</p>
            </div>
            <div className="relative flex flex-wrap gap-3 md:justify-end">
              <Link to="/iletisim" className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors">İletişime Geç <ArrowRight size={18}/></Link>
              <Link to={`/hizmetler/${next.slug}`} className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors">
                Sonraki: {next.title}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
